import React from "react";
import cx from "classnames";
import {
    Switch,
    Route,
    Redirect,
    useLocation
} from "react-router-dom";

import PerfectScrollbar from "perfect-scrollbar";
import "perfect-scrollbar/css/perfect-scrollbar.css";

import { makeStyles } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Fab from "@material-ui/core/Fab";
import Tooltip from "@material-ui/core/Tooltip";
import Zoom from "@material-ui/core/Zoom";
import Fade from "@material-ui/core/Fade";

import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";

import Footer from "components/Footer/Footer.js";
import Sidebar from "components/Sidebar/Sidebar.js";
import LegalAcknowledgementDialog from
    "components/Legal/LegalAcknowledgementDialog.jsx";

import routes from "routes.js";

import styles from
    "assets/jss/material-dashboard-pro-react/layouts/adminStyle.js";

import watermark from "assets/img/boersenkusecpixabay.png";

import languageEn from "assets/img/flags/GB.png";
import languageDe from "assets/img/flags/DE.png";
import languageFr from "assets/img/flags/FR.png";
import languageEs from "assets/img/flags/ES.png";
import languageUk from "assets/img/flags/UA.png";
import languageTr from "assets/img/flags/TR.png";

import { useAuth } from "../auth/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";

var ps;

const useStyles = makeStyles(styles);

const DEFAULT_LANGUAGE = "en";
const LANGUAGE_STORAGE_KEY = "kimat-language";

const GUEST_ALLOWED_PATHS = [
    "/admin/aktiennews",
    "/admin/impressum",
    "/admin/datenschutz",
    "/admin/nutzungsbedingungen",
    "/admin/disclaimer",
    "/admin/risikohinweis",
    "/admin/ki-hinweis",
    "/admin/hilfe"
];

const LANGUAGES = [
    {
        code: "en",
        label: "English",
        icon: languageEn
    },
    {
        code: "de",
        label: "Deutsch",
        icon: languageDe
    },
    {
        code: "fr",
        label: "Français",
        icon: languageFr
    },
    {
        code: "es",
        label: "Español",
        icon: languageEs
    },
    {
        code: "uk",
        label: "Українська",
        icon: languageUk
    },
    {
        code: "tr",
        label: "Türkçe",
        icon: languageTr
    }
];

function normalizeLanguage(value) {
    const normalized = String(value || "")
        .trim()
        .toLowerCase()
        .split("-")[0];

    if (normalized === "ua") {
        return "uk";
    }

    return LANGUAGES.some(
        language => language.code === normalized
    )
        ? normalized
        : DEFAULT_LANGUAGE;
}

function isGuestPathAllowed(pathname) {
    return GUEST_ALLOWED_PATHS.some(
        path =>
            pathname === path ||
            pathname.startsWith(`${path}/`)
    );
}

export default function Admin(props) {
    const { ...rest } = props;

    const classes = useStyles();

    const mainPanel = React.useRef(null);
    const longPressTimer = React.useRef(null);

    const isTablet = useMediaQuery(
        "(max-width:1024px)"
    );

    const location = useLocation();
    const { isGuest } = useAuth();

    const {
        language,
        setLanguage
    } = useLanguage();

    /*
     * Gäste verwenden nur für die Darstellung Englisch.
     * Die gespeicherte Benutzersprache wird dabei nicht überschrieben.
     */
    const effectiveLanguage = isGuest
        ? DEFAULT_LANGUAGE
        : normalizeLanguage(language);

    const [mobileOpen, setMobileOpen] =
        React.useState(false);

    const [sidebarOpen, setSidebarOpen] =
        React.useState(false);

    const [miniActive] = React.useState(false);
    const [color] = React.useState("blue");
    const [bgColor] = React.useState("black");

    const [pageVisible, setPageVisible] =
        React.useState(true);

    React.useEffect(() => {
        if (isTablet) {
            setSidebarOpen(false);
        }
    }, [isTablet]);

    React.useEffect(() => {
        if (
            navigator.platform.indexOf("Win") > -1 &&
            sidebarOpen &&
            mainPanel.current
        ) {
            ps = new PerfectScrollbar(
                mainPanel.current,
                {
                    suppressScrollX: true,
                    suppressScrollY: false
                }
            );

            document.body.style.overflow = "hidden";
        }

        window.addEventListener(
            "resize",
            resizeFunction
        );

        return () => {
            if (
                navigator.platform.indexOf("Win") > -1 &&
                ps
            ) {
                ps.destroy();
                ps = null;
            }

            window.removeEventListener(
                "resize",
                resizeFunction
            );

            document.body.style.overflow = "";
        };
    }, [sidebarOpen]);

    React.useEffect(() => {
        setPageVisible(false);

        const timer = window.setTimeout(() => {
            setPageVisible(true);
        }, 120);

        setMobileOpen(false);

        if (isTablet) {
            setSidebarOpen(false);
        }

        return () => {
            window.clearTimeout(timer);
        };
    }, [location.pathname, isTablet]);

    React.useEffect(() => {
        document.body.classList.add(
            "app-watermark"
        );

        document.body.style.setProperty(
            "--app-watermark-img",
            `url(${watermark})`
        );

        return () => {
            document.body.classList.remove(
                "app-watermark"
            );

            document.body.style.removeProperty(
                "--app-watermark-img"
            );
        };
    }, []);

    React.useEffect(() => {
        document.documentElement.lang =
            effectiveLanguage;
    }, [effectiveLanguage]);

    const resizeFunction = () => {
        if (window.innerWidth >= 960) {
            setMobileOpen(false);
        }
    };

    const getRoutes = routeItems =>
        routeItems.map((prop, key) => {
            if (prop.collapse) {
                return getRoutes(prop.views || []);
            }

            if (prop.layout !== "/admin") {
                return null;
            }

            const completePath =
                `${prop.layout}${prop.path}`;

            if (
                isGuest &&
                prop.path &&
                !isGuestPathAllowed(completePath)
            ) {
                return null;
            }

            return (
                <Route
                    path={completePath}
                    component={prop.component}
                    key={key}
                />
            );
        });

    const getRoute = () =>
        window.location.pathname !==
        "/admin/full-screen-maps";

    const handleSidebarContextMenu = event => {
        event.preventDefault();

        setSidebarOpen(
            previousValue => !previousValue
        );
    };

    const handleSidebarTouchStart = event => {
        if (!("ontouchstart" in window)) {
            return;
        }

        const touchX =
            event.touches?.[0]?.clientX ?? 9999;

        if (touchX > 40) {
            return;
        }

        longPressTimer.current =
            window.setTimeout(() => {
                setSidebarOpen(true);
            }, 600);
    };

    const handleSidebarTouchEnd = () => {
        if (!longPressTimer.current) {
            return;
        }

        window.clearTimeout(
            longPressTimer.current
        );

        longPressTimer.current = null;
    };

    const handleSidebarToggle = () => {
        const nextValue = !sidebarOpen;

        setSidebarOpen(nextValue);

        if (isTablet) {
            setMobileOpen(nextValue);
        }
    };

    const handleLanguageChange = languageCode => {
        if (isGuest) {
            return;
        }

        const nextLanguage =
            normalizeLanguage(languageCode);

        /*
         * Zuerst Context aktualisieren.
         */
        setLanguage(nextLanguage);

        /*
         * Zusätzlich direkt speichern.
         * Der LanguageContext sollte denselben Schlüssel verwenden.
         */
        try {
            localStorage.setItem(
                LANGUAGE_STORAGE_KEY,
                nextLanguage
            );
        } catch (error) {
            console.warn(
                "Sprache konnte nicht gespeichert werden:",
                error
            );
        }
    };

    const renderAdminSwitch = () => (
        <Switch>
            {getRoutes(routes)}

            {isGuest ? (
                <>
                    <Redirect
                        exact
                        from="/admin"
                        to="/admin/aktiennews"
                    />

                    <Redirect
                        to="/admin/aktiennews"
                    />
                </>
            ) : (
                <Redirect
                    exact
                    from="/admin"
                    to="/admin/aktiennews"
                />
            )}
        </Switch>
    );

    const renderLanguageSelector = () => {
        /*
         * Gäste sehen keinerlei Sprachsymbole.
         */
        if (isGuest) {
            return null;
        }

        return (
            <div
                className="kimat-language-selector"
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 7,
                    marginTop: 10
                }}
            >
                {LANGUAGES.map(item => {
                    const isSelected =
                        effectiveLanguage === item.code;

                    return (
                        <Tooltip
                            key={item.code}
                            title={item.label}
                            placement="left"
                        >
                            <button
                                type="button"
                                aria-label={item.label}
                                aria-pressed={isSelected}
                                onClick={() =>
                                    handleLanguageChange(
                                        item.code
                                    )
                                }
                                style={{
                                    width: 40,
                                    height: 40,
                                    padding: 5,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: 13,

                                    border: isSelected
                                        ? "1px solid rgba(219,233,255,0.9)"
                                        : "1px solid rgba(255,255,255,0.08)",

                                    background: isSelected
                                        ? "rgba(32,52,88,0.72)"
                                        : "rgba(20,32,52,0.34)",

                                    backdropFilter:
                                        "blur(16px)",

                                    WebkitBackdropFilter:
                                        "blur(16px)",

                                    boxShadow: isSelected
                                        ? "0 10px 28px rgba(0,0,0,0.38), 0 0 0 2px rgba(219,233,255,0.14)"
                                        : "0 7px 20px rgba(0,0,0,0.24)",

                                    cursor: "pointer",

                                    opacity: isSelected
                                        ? 1
                                        : 0.84,

                                    transform: isSelected
                                        ? "scale(1.08)"
                                        : "scale(1)",

                                    transition:
                                        "transform .25s ease, opacity .25s ease, background .25s ease, border-color .25s ease, box-shadow .25s ease",

                                    outline: "none"
                                }}
                                onMouseEnter={event => {
                                    event.currentTarget.style.opacity =
                                        "1";

                                    event.currentTarget.style.transform =
                                        "translateY(-2px) scale(1.08)";

                                    event.currentTarget.style.background =
                                        "rgba(32,52,88,0.68)";
                                }}
                                onMouseLeave={event => {
                                    event.currentTarget.style.opacity =
                                        isSelected
                                            ? "1"
                                            : "0.84";

                                    event.currentTarget.style.transform =
                                        isSelected
                                            ? "scale(1.08)"
                                            : "scale(1)";

                                    event.currentTarget.style.background =
                                        isSelected
                                            ? "rgba(32,52,88,0.72)"
                                            : "rgba(20,32,52,0.34)";
                                }}
                            >
                                <img
                                    src={item.icon}
                                    alt={item.label}
                                    draggable={false}
                                    style={{
                                        width: 29,
                                        height: 29,
                                        display: "block",
                                        objectFit: "cover",
                                        borderRadius: "50%",
                                        pointerEvents: "none",

                                        filter: isSelected
                                            ? "brightness(1.08) saturate(1.08)"
                                            : "brightness(0.94)"
                                    }}
                                />
                            </button>
                        </Tooltip>
                    );
                })}
            </div>
        );
    };

    return (
        <div className={classes.wrapper}>
            {sidebarOpen && (
                <div
                    style={{
                        animation:
                            "sidebarFadeIn 0.28s ease"
                    }}
                >
                    <Sidebar
                        routes={routes}
                        logoText="K.I.M.A.T."
                        open={mobileOpen}
                        handleDrawerToggle={() => {
                            setMobileOpen(false);
                            setSidebarOpen(false);
                        }}
                        color={color}
                        bgColor={bgColor}
                        miniActive={miniActive}
                        {...rest}
                    />
                </div>
            )}

            <div
                className={
                    classes.mainPanel +
                    " " +
                    cx({
                        [classes.mainPanelSidebarMini]:
                            miniActive,

                        [classes.mainPanelWithPerfectScrollbar]:
                            navigator.platform.indexOf(
                                "Win"
                            ) > -1 && sidebarOpen
                    })
                }
                ref={mainPanel}
                onContextMenu={
                    handleSidebarContextMenu
                }
                onTouchStart={
                    handleSidebarTouchStart
                }
                onTouchEnd={
                    handleSidebarTouchEnd
                }
                onTouchCancel={
                    handleSidebarTouchEnd
                }
                style={{
                    width:
                        sidebarOpen && !isTablet
                            ? "calc(100% - 260px)"
                            : "100%",

                    transition:
                        "width .42s cubic-bezier(.22,.61,.36,1), transform .42s cubic-bezier(.22,.61,.36,1)",

                    position: "relative",
                    minHeight: "100vh"
                }}
            >
                <LegalAcknowledgementDialog />

                {getRoute() ? (
                    <Fade
                        in={pageVisible}
                        timeout={320}
                    >
                        <div className={classes.content}>
                            <div
                                className={
                                    classes.container
                                }
                                style={{
                                    animation:
                                        "contentSlideUp .34s ease"
                                }}
                            >
                                {renderAdminSwitch()}
                            </div>
                        </div>
                    </Fade>
                ) : (
                    <Fade
                        in={pageVisible}
                        timeout={320}
                    >
                        <div className={classes.map}>
                            {renderAdminSwitch()}
                        </div>
                    </Fade>
                )}

                {getRoute() ? (
                    <Footer fluid />
                ) : null}

                <Zoom in timeout={500}>
                    <div
                        style={{
                            position: "fixed",
                            top: 14,
                            right: 18,
                            zIndex: 2100,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            opacity: 0.94
                        }}
                    >
                        <Tooltip
                            title={
                                sidebarOpen
                                    ? "Menü schließen"
                                    : "Menü öffnen"
                            }
                            placement="left"
                        >
                            <Fab
                                onClick={
                                    handleSidebarToggle
                                }
                                style={{
                                    width: 48,
                                    height: 48,
                                    minHeight: 48,
                                    borderRadius: 14,

                                    background:
                                        "rgba(20,32,52,0.52)",

                                    backdropFilter:
                                        "blur(16px)",

                                    WebkitBackdropFilter:
                                        "blur(16px)",

                                    border:
                                        "1px solid rgba(255,255,255,0.08)",

                                    boxShadow:
                                        "0 12px 30px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.05)",

                                    color: "#dbe9ff",

                                    transition:
                                        "transform .35s ease, box-shadow .35s ease, background .35s ease, opacity .35s ease",

                                    opacity: 0.94
                                }}
                                onMouseEnter={event => {
                                    event.currentTarget.style.transform =
                                        "translateY(-3px) scale(1.05)";

                                    event.currentTarget.style.background =
                                        "rgba(32,52,88,0.62)";

                                    event.currentTarget.style.boxShadow =
                                        "0 18px 42px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.08)";

                                    event.currentTarget.style.opacity =
                                        "1";
                                }}
                                onMouseLeave={event => {
                                    event.currentTarget.style.transform =
                                        "translateY(0) scale(1)";

                                    event.currentTarget.style.background =
                                        "rgba(20,32,52,0.52)";

                                    event.currentTarget.style.boxShadow =
                                        "0 12px 30px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.05)";

                                    event.currentTarget.style.opacity =
                                        "0.94";
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent:
                                            "center",

                                        transform:
                                            sidebarOpen
                                                ? "rotate(0deg)"
                                                : "rotate(180deg)",

                                        transition:
                                            "transform .4s cubic-bezier(.25,.8,.25,1)"
                                    }}
                                >
                                    {sidebarOpen ? (
                                        <ChevronLeftIcon />
                                    ) : (
                                        <MenuIcon />
                                    )}
                                </div>
                            </Fab>
                        </Tooltip>

                        {renderLanguageSelector()}
                    </div>
                </Zoom>

                <style>
                    {`
                        @keyframes contentSlideUp {
                            from {
                                opacity: 0;
                                transform: translateY(10px);
                            }

                            to {
                                opacity: 1;
                                transform: translateY(0);
                            }
                        }

                        @keyframes sidebarFadeIn {
                            from {
                                opacity: 0;
                                transform: translateX(-10px);
                            }

                            to {
                                opacity: 1;
                                transform: translateX(0);
                            }
                        }

                        @media (max-width: 600px) {
                            .kimat-language-selector {
                                transform: scale(0.92);
                                transform-origin: top center;
                            }
                        }
                    `}
                </style>
            </div>
        </div>
    );
}